import { useFacilityContext } from '@aspendental/shared-components-web';
import { FacilityFieldsFragment, NearbyFacility } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { BffBrandType, BffFacility, useBffNearbyFacilitiesByZipcodeQuery } from '@src/lib/__generated/beffe/sdk';
import { useAppointmentInformationStore } from '@src/store';
import makeRequestHeaders from '@src/utils/make-request-headers';

export const QUERY_KEY = 'consultAppointments';

const fetchAppointments = async ({ leadId, centerId, startDate, salesforceId, answers, sessionId }, signal) => {
	const queryString = `?lead_id=${leadId}&center_id=${centerId}&start_date=${startDate}&sf_id=${
		salesforceId ?? ''
	}&answers=${JSON.stringify(answers)}`;

	try {
		const response = await fetch(process.env.APP_PATH + '/api/clear-choice/get-appointments/' + queryString, {
			headers: makeRequestHeaders({ sessionId }),
			method: 'GET',
			signal,
		});
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
		}
		return await response.json();
	} catch (error) {
		console.error('Error fetching appointments:', error);
		throw error;
	}
};

const mapNearbyOfficeDistance = (centerList: NearbyFacility[] | null) => {
	return centerList?.map((center) => ({
		...center.facility,
		location: { ...center.facility.location, distance: center.distanceInMiles },
	}));
};

export function useConsultAppointments({ leadId, salesforceId, zipCode, startDate, answers, sessionId = 'N/A' }) {
	const [radiusMiles, setRadiusMiles] = useState<number>(250);
	const [initialNearbyLocations, setInitialNearbyLocations] = useState<BffFacility[] | FacilityFieldsFragment[]>([]);
	const searchParams = useSearchParams();
	const facilityDetails = useFacilityContext();
	const { chosenCenterData, setChosenCenterData, shouldPreserveAppointmentAndCenterData } =
		useAppointmentInformationStore((store) => store);

	const centerId = chosenCenterData?.code?.replace('CC_', '');

	const [{ data: facilitiesByZip, error: errorFacilitiesByZip, fetching: loadingFacilitiesByZip }] =
		useBffNearbyFacilitiesByZipcodeQuery({
			variables: {
				zipCode,
				brand: BffBrandType.ClearChoice,
				radiusMiles,
			},
			pause: searchParams.has('fc'),
		});

	const {
		error: errorAppointments,
		isLoading: loadingAppointments,
		status: loadingApptStatus,
		data,
	} = useQuery({
		queryKey: [QUERY_KEY, leadId, centerId, startDate, salesforceId, JSON.stringify(answers)],
		queryFn: ({ signal }) =>
			fetchAppointments({ leadId, centerId, startDate, salesforceId, answers, sessionId }, signal),
		enabled: !!leadId && !!centerId,
	});

	useEffect(() => {
		if (facilitiesByZip?.facilitiesByZipcode) {
			if (!facilitiesByZip?.facilitiesByZipcode?.length && !loadingFacilitiesByZip && radiusMiles !== 500) {
				//retry with 500 mile radius if no facilities found with initial query
				setRadiusMiles(500);
			} else {
				const nearbyFacilities = facilitiesByZip?.facilitiesByZipcode;
				const [initialCenter] = nearbyFacilities;
				if (!shouldPreserveAppointmentAndCenterData) {
					setChosenCenterData(initialCenter);
				}
				if (nearbyFacilities.length) {
					setInitialNearbyLocations(nearbyFacilities as BffFacility[]);
				}
			}
		} else if (searchParams.has('fc')) {
			// use facility from context
			setChosenCenterData(facilityDetails?.officeInfo);
			const nearbyCenters = mapNearbyOfficeDistance(
				facilityDetails?.locationState?.nearByOfficeLocations ?? []
			) as BffFacility[];
			if (nearbyCenters.length) {
				setInitialNearbyLocations(nearbyCenters);
			}
		}
	}, [
		facilitiesByZip,
		facilityDetails?.locationState?.nearByOfficeLocations,
		facilityDetails?.officeInfo,
		loadingFacilitiesByZip,
		radiusMiles,
		searchParams,
		setChosenCenterData,
		shouldPreserveAppointmentAndCenterData,
	]);

	return {
		initialNearbyLocations,
		errorAppointments,
		loadingFacilitiesByZip,
		errorFacilitiesByZip,
		loadingAppointments,
		loadingApptStatus,
		data,
	};
}
